/**
 * Payment Handlers Module
 * Manages payment flow, notifications, and order processing
 */

import { NotificationManager } from '../utils/notifications.js';

export class PaymentHandlers {
  constructor(app) {
    this.app = app;
  }

  async handleOrderConfirmation(orderData) {
    console.log('[MainApp] Order confirmation - NEW UX FLOW');

    try {
      const cartItems = this.app.cartManager.getItems();
      const menuData = this.app.configManager.getMenuData();

      const orderPayload = this.app.orderService.createOrderPayload(
        cartItems,
        orderData.orderTotals,
        menuData,
        {
          type: 'dine-in',
          specialInstructions: orderData.specialInstructions || ''
        }
      );

      const createdOrder = await this.app.orderService.createOrder(orderPayload);
      console.log('[MainApp] Order created successfully:', createdOrder.id);

      this.app.currentOrder = createdOrder;
      this.app.cartManager.setPendingOrder(createdOrder);

      const chosen = (orderData.paymentMethod || '').toLowerCase();
      const isPayLater = chosen === 'cash' || chosen === 'desk_payment' || chosen === 'staff_payment';

      if (isPayLater) {
        const currency = createdOrder.currency || this.app.configManager.getCurrentConfig()?.currency || 'PLN';
        const items = Array.isArray(createdOrder.items) ? createdOrder.items : [];
        const total = (typeof createdOrder.total === 'number' && createdOrder.total) || 0;
        const successOrderData = {
          id: createdOrder.id || null,
          orderNumber: createdOrder.orderNumber || null,
          items,
          total,
          currency
        };
        try { this.app.orderSuccessModal?.show(successOrderData); } catch {}
        this.showPaymentNotification('Pay online, at the bar, or with staff', 'required');
      } else {
        this.showPaymentNotification('Payment Required', 'required');
        this.app.unifiedPaymentModal.show(createdOrder, () => {
          this.hidePaymentNotification();
        });
      }

    } catch (error) {
      console.error('[MainApp] Failed to create order:', error);

      const confirmButton = document.getElementById('confirm-order-btn');
      if (confirmButton) {
        confirmButton.disabled = false;
        confirmButton.textContent = this.app.orderConfirmationModal?.getConfirmButtonText() || 'Place Order';
      }

      this.showPaymentNotification('Order creation failed', 'error');
      setTimeout(() => this.hidePaymentNotification(), 3000);

      throw error;
    }
  }

  async initiatePayment(order, orderData) {
    try {
      console.log('[MainApp] Initiating payment:', orderData.paymentMethod, orderData.orderTotals.total);

      const paymentData = await this.app.paymentService.initiatePayment(
        order,
        orderData.paymentMethod,
        orderData.orderTotals.total
      );

      this.app.paymentProcessingModal.show(paymentData);

    } catch (error) {
      console.error('Failed to initiate payment:', error);

      this.app.paymentResultModal.showFailure(
        {
          method: orderData.paymentMethod,
          amount: orderData.orderTotals.total,
          currency: order.currency || 'PLN'
        },
        {
          message: error.message || 'Payment could not be initiated',
          code: error.code || 'PAYMENT_INITIATION_FAILED'
        }
      );
    }
  }

  handlePaymentSuccess(paymentData) {
    console.log('[MainApp] Payment success - NEW UX:', paymentData);

    this.app.cartManager.completeOrder();
    this.app.menuView.updateCartCount(0);

    const order = this.app.currentOrder || {};
    const currency = order.currency || this.app.configManager.getCurrentConfig()?.currency || 'PLN';
    const items = Array.isArray(order.items) ? order.items : [];
    const total = (
      (typeof order.total === 'number' && order.total) ||
      (order.total && order.total.amount) ||
      (paymentData?.amount?.amount) ||
      (paymentData?.amount) ||
      (this.app.cartManager.getTotals()?.total || 0)
    );

    const successOrderData = {
      id: order.id || null,
      orderNumber: order.orderNumber || null,
      items,
      total,
      currency,
      digitalReceipt: this.app.paymentService?.digitalReceiptPreference || null
    };

    try {
      this.app.orderSuccessModal?.show(successOrderData);
    } catch (e) {
      console.warn('[MainApp] Failed to show OrderSuccessModal, falling back to notification:', e);
      this.showPaymentNotification('Payment completed successfully!', 'success');
    }
  }

  handlePaymentRetry(payment) {
    console.log('[MainApp] Payment retry requested:', payment);

    if (this.app.currentOrder) {
      this.initiatePayment(this.app.currentOrder, {
        paymentMethod: payment.method,
        orderTotals: { total: payment.amount }
      });
    } else {
      console.error('[MainApp] Cannot retry payment - no current order');
      NotificationManager.error('Unable to retry payment. Please start a new order.');
    }
  }

  handlePaymentCancellation(payment) {
    console.log('[MainApp] Payment cancelled:', payment);

    this.app.cartManager.cancelPendingOrder();
    this.app.menuView.updateCartCount(this.app.cartManager.getItemCount());

    NotificationManager.info('Payment cancelled. Your items are still in the cart.');

    this.hidePaymentNotification();
  }

  handlePaymentResultAction(action, result) {
    console.log('[MainApp] Payment result action:', action, result);

    switch (action) {
      case 'continue':
        this.app.navigateToWelcome();
        break;

      case 'retry':
        if (this.app.currentOrder) {
          this.initiatePayment(this.app.currentOrder, {
            paymentMethod: result.payment.method,
            orderTotals: { total: result.payment.amount }
          });
        }
        break;

      case 'change_method':
        if (this.app.currentOrder) {
          console.log('[MainApp] Payment method change - showing UnifiedPaymentModal (NEW UX)');
          this.showPaymentNotification('Online Payment Required', 'required');
          this.app.unifiedPaymentModal.show(this.app.currentOrder, () => {
            this.hidePaymentNotification();
          });
        }
        break;

      case 'cancel':
        this.app.navigateToWelcome();
        break;
    }

    this.app.currentOrder = null;
  }

  showPaymentNotification(message, status = 'required') {
    console.log('[MainApp] Showing payment notification:', message, status);

    requestAnimationFrame(() => {
      const header = this.getCurrentHeader();
      if (header) {
        const clickable = !!this.app.currentOrder;
        header.updatePaymentNotification(true, message, status, clickable ? () => {
          this.app.unifiedPaymentModal.show(this.app.currentOrder, () => {
            this.hidePaymentNotification();
          });
        } : null);
      } else {
        console.debug('[MainApp] Header not found, retrying in 100ms...');
        if (!this.app.paymentNotificationRetries) this.app.paymentNotificationRetries = 0;
        if (this.app.paymentNotificationRetries < 5) {
          this.app.paymentNotificationRetries++;
          setTimeout(() => this.showPaymentNotification(message, status), 100);
        } else {
          console.error('[MainApp] Failed to show payment notification after 5 attempts');
          this.app.paymentNotificationRetries = 0;
        }
      }
    });
  }

  hidePaymentNotification() {
    console.log('[MainApp] Hiding payment notification');

    const header = this.getCurrentHeader();
    if (header) {
      header.updatePaymentNotification(false);
    }
  }

  getCurrentHeader() {
    if (this.app.menuView?.layoutManager?.currentLayout?.header) {
      console.log('[MainApp] Found header in current layout');
      return this.app.menuView.layoutManager.currentLayout.header;
    }

    if (this.app.menuView?.header) {
      console.log('[MainApp] Found header via direct access');
      return this.app.menuView.header;
    }

    const tableNumberBar = document.querySelector('.table-number-bar');
    if (tableNumberBar) {
      console.log('[MainApp] Found table number bar:', tableNumberBar);
      return {
        updatePaymentNotification: (show, message, status, onClick) => {
          const parent = tableNumberBar.parentNode;
          if (!parent) return;

          let notificationContainer = parent.querySelector('.payment-notification-container');
          if (!notificationContainer) {
            notificationContainer = document.createElement('div');
            notificationContainer.className = 'payment-notification-container';
            if (tableNumberBar.nextSibling) {
              parent.insertBefore(notificationContainer, tableNumberBar.nextSibling);
            } else {
              parent.appendChild(notificationContainer);
            }
          }

          if (show) {
            const statusClass = `payment-notification-${status}`;
            const icon = this.getPaymentNotificationIcon(status);
            notificationContainer.innerHTML = `
              <div class="topbar-payment-notification ${statusClass}" id="topbar-payment-notification">
                <div class="payment-notification-content">
                  <i class="fas ${icon}"></i>
                  <span class="payment-notification-message">${message}</span>
                </div>
              </div>
            `;
            const el = document.getElementById('topbar-payment-notification');
            if (el) {
              if (onClick) {
                el.style.cursor = 'pointer';
                el.addEventListener('click', onClick);
              } else {
                el.style.cursor = 'default';
              }
            }
          } else if (notificationContainer) {
            notificationContainer.innerHTML = '';
          }
        }
      };
    }

    if (!this.app.headerWarningShown) {
      console.debug('[MainApp] Table number bar not found - payment notifications will not be shown');
      this.app.headerWarningShown = true;
    }
    return null;
  }

  getPaymentNotificationIcon(status) {
    const icons = {
      'required': 'fa-credit-card',
      'pending': 'fa-clock',
      'processing': 'fa-spinner fa-spin',
      'success': 'fa-check-circle',
      'error': 'fa-exclamation-triangle',
      'idle': 'fa-info-circle'
    };
    return icons[status] || 'fa-info-circle';
  }

  handlePaymentSessionTimeout(order) {
    console.log('[MainApp] Payment session timeout for order:', order.id);

    this.hidePaymentNotification();
    this.showPaymentNotification('Payment session expired. Please try again.', 'error');

    setTimeout(() => {
      this.hidePaymentNotification();
    }, 5000);
  }

  handleStaffNotification(type, color, message, orderId) {
    console.log('[MainApp] Staff notification:', { type, color, message, orderId });

    const statusMessages = {
      red: 'Staff assistance requested',
      yellow: 'Desk payment required',
      green: 'Payment completed successfully'
    };

    const notificationMessage = statusMessages[color] || message;

    this.showPaymentNotification(notificationMessage,
      color === 'green' ? 'success' :
      color === 'red' ? 'pending' : 'processing'
    );

    if (color !== 'red') {
      setTimeout(() => {
        if (color === 'green') {
          this.hidePaymentNotification();
          this.app.navigateToWelcome();
        }
      }, 3000);
    }
  }
}
